import type { MessageKey, Role } from "@mandrac/shared";
import type { Me } from "../../lib/session";

/**
 * Office pages and who may open them: the one source of truth for the top nav, the route guards
 * and the port selector. The role lists mirror the API's checks for each page's main data:
 *
 * - overview:  GET /dashboard, /reports/*            authority_admin, port_manager, accountant
 * - plan:      GET /ports/:id/occupancy              authority_admin, port_manager (+ dock_staff, not an office role)
 * - stays:     GET /stays                            authority_admin, port_manager
 * - permanent: GET /contracts, /absences, occupancy  authority_admin, port_manager (accountant may read
 *              contracts, but absences and occupancy answer 403, so the page is not for accountants)
 * - waiting:   GET /waiting-list                     authority_admin, port_manager
 * - invoices:  GET /invoices, /exports/*             authority_admin, port_manager, accountant
 * - settings:  authority_admin and port_manager (a manager only for their own ports) see the tabs
 *              ports, tariffs, users, registry and import; authority and audit are authority_admin
 *              only; platform admins get the platform tab
 */
export type AdminPageId = "overview" | "plan" | "stays" | "permanent" | "waiting" | "invoices" | "settings";

export interface AdminPage {
  id: AdminPageId;
  /** Route segment under /admin; "" is the index route. */
  segment: string;
  /** Absolute path. */
  path: string;
  navKey: MessageKey;
  roles: readonly Role[];
  /** Platform admins may open the page even without a role in an authority. */
  platformAdmin?: boolean;
  /** The page can show every port at once, so the port selector offers "Sve luke" there. */
  allPorts?: boolean;
}

const page = (id: AdminPageId, segment: string, navKey: MessageKey, roles: readonly Role[], extra: Partial<AdminPage> = {}): AdminPage => ({
  id,
  segment,
  path: segment ? `/admin/${segment}` : "/admin",
  navKey,
  roles,
  ...extra,
});

const EDITORS: readonly Role[] = ["authority_admin", "port_manager"];
const OFFICE: readonly Role[] = ["authority_admin", "port_manager", "accountant"];

/** In nav order. */
export const ADMIN_PAGES: readonly AdminPage[] = [
  page("overview", "", "nav.overview", OFFICE, { allPorts: true }),
  page("plan", "plan", "nav.plan", EDITORS),
  page("stays", "stays", "nav.stays", EDITORS),
  page("permanent", "permanent", "nav.permanent", EDITORS),
  page("waiting", "waiting-list", "nav.waiting", EDITORS),
  page("invoices", "invoices", "nav.invoices", OFFICE, { allPorts: true }),
  page("settings", "settings", "nav.settings", EDITORS, { platformAdmin: true }),
];

export function adminPage(id: AdminPageId): AdminPage {
  return ADMIN_PAGES.find((p) => p.id === id)!;
}

type Viewer = Pick<Me, "roles" | "isPlatformAdmin" | "deviceId">;

export function canOpenPage(me: Viewer | null | undefined, p: AdminPage | AdminPageId): boolean {
  if (!me) return false;
  const def = typeof p === "string" ? adminPage(p) : p;
  if (me.roles.some((r) => def.roles.includes(r))) return true;
  // A request that carries a dock device never has platform rights (the API strips them); the office sends none.
  return !!def.platformAdmin && me.isPlatformAdmin && !me.deviceId;
}

/** Pages the user may open, in nav order. */
export function allowedPages(me: Viewer | null | undefined): AdminPage[] {
  return ADMIN_PAGES.filter((p) => canOpenPage(me, p));
}

/** The page a pathname belongs to (`/admin/settings` → settings), or undefined. */
export function pageAt(pathname: string): AdminPage | undefined {
  const rest = pathname.replace(/\/+$/, "");
  if (rest === "/admin") return ADMIN_PAGES[0];
  const m = /^\/admin\/([^/]+)/.exec(rest);
  return m ? ADMIN_PAGES.find((p) => p.segment === m[1]) : undefined;
}

export type GuardResult = { kind: "allow" } | { kind: "redirect"; to: string } | { kind: "deny"; fallback: AdminPage | undefined };

/**
 * What the route guard does for a page: render it, redirect (only the index route, to the first
 * page the user may open, e.g. settings for a platform admin outside any authority), or show
 * "no access" with a link to that first page.
 */
export function guardPage(me: Viewer | null | undefined, id: AdminPageId): GuardResult {
  const def = adminPage(id);
  if (canOpenPage(me, def)) return { kind: "allow" };
  const fallback = allowedPages(me)[0];
  if (def.segment === "" && fallback) return { kind: "redirect", to: fallback.path };
  return { kind: "deny", fallback };
}

// ---------------------------------------------------------------------------
// Port selection: one selector in the top bar, "Sve luke" only where the page supports it.

export const ALL_PORTS = "all";

export interface PortChoice {
  /** The selected port, for pages that work on one port and for new records. */
  portId: string | null;
  /** "Sve luke" is selected (needs more than one port). */
  allPorts: boolean;
  /** Filter for pages that can show every port: null = all ports. */
  scopePortId: string | null;
}

/** Resolve the stored choice against the user's ports (a stored port may be gone or out of scope). */
export function resolvePortChoice(ports: ReadonlyArray<{ id: string }>, storedPortId: string | null, allPortsPref: boolean): PortChoice {
  const portId = ports.find((p) => p.id === storedPortId)?.id ?? ports[0]?.id ?? null;
  const allPorts = allPortsPref && ports.length > 1;
  return { portId, allPorts, scopePortId: allPorts ? null : portId };
}

/** The selector on a given page: whether it offers "Sve luke" and which value it shows. */
export function portSelector(page: AdminPage | undefined, portCount: number, choice: PortChoice): { offerAll: boolean; value: string } {
  const offerAll = !!page?.allPorts && portCount > 1;
  return { offerAll, value: offerAll && choice.allPorts ? ALL_PORTS : (choice.portId ?? "") };
}
